<template>
    <div v-if="form" class="checkout-flight-form">
        <PassportExamples />

        <FormV2
            :id="formId"
            :key="formKey"
            :initialValues="form.initialValues"
            :validationSchema="form.validationSchema"
            :onValidChange="handleValidChange"
            :onChange="handleFormChange">
            <div class="checkout-flight-form-paxes">
                <Pax
                    v-for="(pax, index) in product?.Pax"
                    :id="sectionId"
                    :key="index"
                    :index="index"
                    :pax="pax"
                    :product="product"
                    :ageRangeDefinitions="ageDefinitions"
                    :toDate="toDate" />
            </div>
            <div v-if="hasSeating" class="checkout-flight-seating">
                <Seating :id="sectionId" :product="product" />
            </div>
        </FormV2>

        <Copy
            :productId="product.Id"
            :formId="formId"
            :formValid="isValid"
            :formValues="formValues" />
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { defineComponent, PropType, ref, watch, computed } from 'vue';
    import { useStore } from '@/store';
    import {
        FlightPax,
        FlightProduct,
        GordianWidgetStatus,
        ProductType,
        SeatAvailability,
    } from '@/api/Cart/cart.interfaces';
    import { getMoneyObjectAsString } from '@shared/helpers/money';
    import { getFieldName } from '@ui/components/BaggageSelectionForm/BaggageSelectionForm.helpers';
    import { ValidationSchema } from '@ui/components/FormV2/Validations/Validations.interfaces';
    import { FormValues } from '@ui/components/FormV2/Form.interfaces';
    import { getAgeConfigByProduct } from '@/helpers/dobValidation';
    import { AgeRangeDefinitions, PassengerType } from '@/api/Checkout/checkout.interfaces';
    import { Baggage, LegType, NO_SELECTION } from '@shared/interfaces/baggage';
    import { isDefined } from '@ui/components/FormV2/Validations/functions';
    import { filterAdditionalBaggage, getBaggagesBySector } from '@shared/helpers/baggage';
    import { preselectBaggageByDefault } from '@/helpers/baggage';
    import { getProductPaxFields } from './FlightPax.helpers';
    import { generateUUID } from '@shared/utils/uuid';
    import { getSeatingFieldName } from './Seating/Seating.helpers';
    import { getProductToDate } from '@/api/Cart/cart.helpers';
    import { CheckoutStepState } from '@/store/checkout/types';
    import { FormContext } from 'vee-validate';
    import FormV2 from '@ui/components/FormV2/Form.vue';
    import Seating from './Seating/Seating.vue';
    import Pax from './Pax/Pax.vue';
    import Copy from '@/components/checkoutPageV3/Shared/Copy/Copy.vue';
    import PassportExamples from './PassportExamples/PassportExamples.vue';

    interface FormState {
        validationSchema: ValidationSchema;
        initialValues: FormValues;
    }

    export default defineComponent({
        name: 'FlightForm',
        methods: { t, getMoneyObjectAsString },
        components: {
            Copy,
            Pax,
            FormV2,
            PassportExamples,
            Seating,
        },
        props: {
            sectionId: { type: String, required: true },
            product: {
                type: Object as PropType<FlightProduct>,
                required: true,
            },
        },
        setup(props) {
            const store = useStore();
            const form = ref<FormState | null>(null);
            const formId = `form_flight_${props.product.Id}`;
            const formKey = ref<string | undefined>(undefined); //generateUUID to re-render the form
            const formValues = ref<FormValues | null>(null);
            const seatingFieldName = getSeatingFieldName();

            const states = computed<CheckoutStepState[]>(
                () => store.getters['checkout/getStates']
            );
            const isValid = computed<boolean>(() => {
                const step = states.value.find((x) => x.id === props.sectionId);
                return step ? step.valid : false;
            });

            const hasSeating = computed<boolean>(() => {
                const hasPostCheckout = !!store.state.cart.data.FlowContainsPostCheckout;

                return Boolean(!hasPostCheckout && props.product?.Gordian);
            });

            const toDate = computed(() => getProductToDate(props.product) ?? new Date());

            const isSeatingUpdating = computed<boolean>(() => {
                return props.product?.Gordian?.WidgetStatus === GordianWidgetStatus.UPDATING;
            });

            const hasInfant = computed(() => !!props.product?.Pax.some(isInfant));

            const isInfant = (pax: FlightPax) => {
                const ageConfig = getAgeConfigByProduct(
                    ageDefinitions.value,
                    ProductType.Flight,
                    pax.PaxDescription?.Age
                );

                return ageConfig.Type === PassengerType.Infant;
            };

            const ageDefinitions = computed<AgeRangeDefinitions>(
                () => store.getters['content/getCheckoutAgeRangeDefinitions']
            );

            const paxFields = computed(() => {
                return props.product
                    ? getProductPaxFields(props.product, ageDefinitions.value)
                    : [];
            });

            watch(
                () => props.product?.Gordian?.SeatAvailability,
                (newAvailability: SeatAvailability | null | undefined) => {
                    setInitialState(newAvailability);
                },
                { immediate: true }
            );

            function handleFormChange({ values }: FormContext) {
                formValues.value = values;
            }

            function setInitialState(availability: SeatAvailability | null | undefined) {
                const state: FormState = {
                    validationSchema: {},
                    initialValues: {},
                };

                const preSelectNoBaggage = preselectBaggageByDefault();

                const pushToState = (
                    bag: Baggage,
                    legType: LegType,
                    paxIndex: number,
                    pax: FlightPax
                ) => {
                    const key = getFieldName(bag.Type, legType, paxIndex);
                    state.validationSchema[key] = isInfant(pax) ? [] : [isDefined];

                    state.initialValues[key] = preSelectNoBaggage ? NO_SELECTION : undefined;
                };

                if (hasSeating.value && availability === SeatAvailability.Available) {
                    state.validationSchema[seatingFieldName] = [isDefined];
                    state.initialValues[seatingFieldName] = undefined;
                }

                props.product?.Pax.forEach((pax: FlightPax, index: number) => {
                    const { Alternatives, SplitSector } = filterAdditionalBaggage(
                        pax.AdditionalBaggage
                    );

                    if (SplitSector) {
                        getBaggagesBySector(Alternatives, 1).forEach((bag) =>
                            pushToState(bag, LegType.Outbound, index, pax)
                        );
                        getBaggagesBySector(Alternatives, 2).forEach((bag) =>
                            pushToState(bag, LegType.Inbound, index, pax)
                        );
                    } else {
                        Alternatives.forEach((bag) =>
                            pushToState(bag, LegType.RoundTrip, index, pax)
                        );
                    }
                });

                paxFields.value.forEach((field) => {
                    state.validationSchema[field.name] = field.validation;
                });

                form.value = state;
                // We do this to reinitialize the form due to intialValues / validationSchema changes
                formKey.value = generateUUID();
            }

            function handleValidChange(valid: boolean) {
                store.dispatch('checkout/setValid', {
                    id: props.sectionId,
                    valid,
                });
            }

            return {
                form,
                formId,
                formKey,
                hasSeating,
                handleValidChange,
                handleFormChange,
                hasInfant,
                isSeatingUpdating,
                ageDefinitions,
                toDate,
                isValid,
                formValues,
            };
        },
    });
</script>

<style lang="scss">
    @forward 'Form.scss';
</style>
